import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { listImportRuns, listKnownDatasets, triggerKnownDatasetImport }
  from "../api/adminApi";
import {
  StatusBadge, ShortId, fmtDate, Section, BarList,
} from "../components/admin/AdminUI";

export default function AdminImportsPage() {
  // Sprint R6 Phase 1 (P0.2) — UI filter for `import_type`.
  // Default "real" hides the 96% of import_runs that are cron
  // `recompute_missing` tickets with counts.received=0. Toggle lets
  // operators see maintenance jobs separately or the legacy "all" view.
  const [importType, setImportType] = useState("real");

  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error,
  } = useInfiniteQuery({
    queryKey: ["admin", "import-runs", importType],
    queryFn: ({ pageParam }) =>
      listImportRuns({ limit: 50, cursor: pageParam, import_type: importType }),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor || undefined,
  });

  const runs = useMemo(
    () => (data?.pages || []).flatMap((p) => p.items || []),
    [data],
  );

  // Imports-per-day bar list, computed client-side from last 14 days of data.
  const perDay = useMemo(() => {
    const map = new Map();
    const cutoff = Date.now() - 14 * 86400 * 1000;
    for (const r of runs) {
      const t = r.started_at ? new Date(r.started_at).getTime() : 0;
      if (!t || t < cutoff) continue;
      const key = new Date(t).toISOString().slice(0, 10);
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries())
      .sort()
      .map(([label, value]) => ({ label, value }));
  }, [runs]);

  const knownDatasets = useQuery({
    queryKey: ["admin", "known-datasets"],
    queryFn: listKnownDatasets,
    staleTime: 60_000,
  });

  const trigger = useMutation({
    mutationFn: (key) => triggerKnownDatasetImport(key),
    onSuccess: (resp) => {
      toast.success(
        `Import triggered (${resp?.import_run_ulid ? resp.import_run_ulid.slice(0, 8) + "…" : "ok"})`,
      );
    },
    onError: (err) => toast.error(String(err?.message || err)),
  });

  const [selectedDataset, setSelectedDataset] = useState("");

  return (
    <div data-testid="admin-imports-page" className="space-y-8">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            /api/admin/import-runs
          </span>
          <h1 className="text-2xl text-zinc-100 font-medium mt-1">Imports</h1>
          <p className="text-xs text-zinc-500 mt-1">
            {importType === "real" && "Externe Apify-/Webhook-Importläufe (Standard)."}
            {importType === "maintenance" && "Wartungs-Cron-Tickets (recompute_missing)."}
            {importType === "all" && "Alle Einträge ungefiltert (Legacy-Ansicht)."}
          </p>
        </div>
        <div
          role="tablist"
          aria-label="Import-Filter"
          data-testid="import-type-filter"
          className="inline-flex rounded-md border border-zinc-800 bg-zinc-900/60 p-0.5 text-xs"
        >
          {[
            ["real", "Echte Imports"],
            ["maintenance", "Wartung"],
            ["all", "Alle"],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              role="tab"
              aria-selected={importType === val}
              data-testid={`import-type-${val}`}
              onClick={() => setImportType(val)}
              className={
                "px-3 py-1.5 rounded transition " +
                (importType === val
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "text-zinc-400 hover:text-zinc-200")
              }
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Per-day chart */}
      <Section title="Imports per day · last 14 days" testid="imports-per-day">
        <BarList items={perDay} />
      </Section>

      {/* Trigger known dataset import */}
      <Section title="Trigger known dataset import" testid="imports-trigger">
        {knownDatasets.isLoading ? (
          <div className="text-xs text-zinc-500">Loading datasets…</div>
        ) : (knownDatasets.data?.items || []).length === 0 ? (
          <div
            data-testid="imports-trigger-empty"
            className="text-xs text-zinc-500 border border-zinc-800 rounded-lg px-3 py-2"
            title={knownDatasets.data?.meta?.reason || ""}
          >
            ⓘ No known datasets configured.&nbsp;
            <span className="text-zinc-600">
              Set <code className="text-emerald-300">APIFY_TOKEN</code> +&nbsp;
              <code className="text-emerald-300">APIFY_KNOWN_DATASETS_JSON</code> to enable.
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <select
              data-testid="imports-trigger-select"
              value={selectedDataset}
              onChange={(e) => setSelectedDataset(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 text-sm text-zinc-200"
            >
              <option value="">— pick a dataset —</option>
              {(knownDatasets.data?.items || []).map((it) => (
                <option key={it.key} value={it.key} disabled={!it.configured}>
                  {it.key} {it.configured ? "" : "(not configured)"}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="imports-trigger-btn"
              disabled={!selectedDataset || trigger.isPending}
              onClick={() => trigger.mutate(selectedDataset)}
              className="px-3 py-1.5 rounded-md bg-emerald-500 text-zinc-950 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {trigger.isPending ? "Triggering…" : "Run now"}
            </button>
          </div>
        )}
      </Section>

      {/* Table */}
      <Section title="Import runs">
        {isLoading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : isError ? (
          <div className="text-rose-400">Error: {String(error?.message || error)}</div>
        ) : runs.length === 0 ? (
          <div data-testid="imports-empty" className="text-xs text-zinc-500">
            No import runs yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">ULID</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Vertical</th>
                  <th className="text-left px-3 py-2">Trigger</th>
                  <th className="text-right px-3 py-2">Recv</th>
                  <th className="text-right px-3 py-2">Raw</th>
                  <th className="text-right px-3 py-2">OK</th>
                  <th className="text-right px-3 py-2">Fail</th>
                  <th className="text-right px-3 py-2">Dupes</th>
                  <th className="text-right px-3 py-2">Created</th>
                  <th className="text-right px-3 py-2">Δ price</th>
                  <th className="text-right px-3 py-2">ms</th>
                  <th className="text-right px-3 py-2">Errors</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {runs.map((r) => {
                  const c = r.counts || {};
                  return (
                    <tr key={r.ulid} className="border-t border-zinc-900/60 hover:bg-zinc-900/30">
                      <td className="px-3 py-2">
                        <Link to={`/admin/imports/${r.ulid}`}
                              className="text-emerald-300 hover:underline">
                          <ShortId value={r.ulid} />
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-zinc-500">{fmtDate(r.started_at)}</td>
                      <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                      <td className="px-3 py-2 text-zinc-400">{r.source_platform}</td>
                      <td className="px-3 py-2 text-zinc-400">{r.vertical}</td>
                      <td className="px-3 py-2 text-zinc-500">{r.trigger}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.received}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.raw_stored}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{c.mapped_success}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-300">{c.mapped_failed}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.duplicates}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.listings_created}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.price_changes_detected}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{r.duration_ms}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{(r.errors || []).length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {hasNextPage && (
          <div className="text-center pt-3">
            <button
              data-testid="imports-load-more"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="px-4 py-1.5 rounded-md border border-zinc-700 text-zinc-200 text-sm hover:border-emerald-500/60 disabled:opacity-40"
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </Section>
    </div>
  );
}
